import { create } from 'zustand';
import { configDb, type DashboardMode } from '@/persistence/config-db';
import type { WidgetConfig } from '@/types/widget';
import type { ColumnMeta } from '@/types/dataset';

type WidgetsState = {
  /** Widgets belonging to the currently-loaded (dataset, mode) pair. */
  widgets: WidgetConfig[];
  /** Which dataset `widgets` corresponds to (null until first load). */
  datasetId: string | null;
  /** Which mode `widgets` corresponds to (null until first load). */
  mode: DashboardMode | null;
  /** True once `loadForDataset` finishes for the current (dataset, mode). */
  loaded: boolean;

  /**
   * Switch to a (dataset, mode) pair and populate `widgets` from
   * IndexedDB. Seeds the default dashboard on first load of a
   * normal-mode dataset that has no persisted widgets — compare-mode
   * starts empty so the user picks a date column first (D-020).
   */
  loadForDataset: (
    id: string,
    columns: ColumnMeta[],
    mode: DashboardMode,
  ) => Promise<void>;
  addWidget: (cfg: WidgetConfig) => Promise<void>;
  updateWidget: (id: string, patch: Partial<WidgetConfig>) => Promise<void>;
  removeWidget: (id: string) => Promise<void>;
  /** Reset state (e.g. when no dataset is active). */
  clear: () => void;
};

function genId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

/**
 * Fold legacy multi-series kinds (`grouped-bar`, `multi-line`) into the
 * unified `bar` / `line` kinds. Returns both the normalised list and the
 * subset that actually changed, so callers can persist only the diff.
 */
function migrateLegacyKinds(
  widgets: WidgetConfig[],
): { all: WidgetConfig[]; changed: WidgetConfig[] } {
  const changed: WidgetConfig[] = [];
  const all = widgets.map((w) => {
    const raw: string = w.kind;
    if (raw === 'grouped-bar') {
      const next = { ...w, kind: 'bar' as const };
      changed.push(next);
      return next;
    }
    if (raw === 'multi-line') {
      const next = { ...w, kind: 'line' as const };
      changed.push(next);
      return next;
    }
    return w;
  });
  return { all, changed };
}

/**
 * Seed configs for a newly-uploaded dataset. Matches the pre-Step-5
 * hardcoded dashboard so users have something to look at immediately.
 * Returns `[]` if the dataset has fewer than 2 columns.
 */
export function buildDefaultWidgets(
  datasetId: string,
  columns: ColumnMeta[],
  mode: DashboardMode = 'normal',
): WidgetConfig[] {
  const first = columns[0];
  const second = columns[1] ?? columns[0];
  if (!first || !second) return [];

  const groupCol = first.name;
  const countCol = second.name;

  return [
    {
      id: genId('kpi'),
      datasetId,
      mode,
      kind: 'kpi',
      title: `Unique ${groupCol}`,
      metric: { column: groupCol, aggregation: 'count_distinct' },
    },
    {
      id: genId('bar'),
      datasetId,
      mode,
      kind: 'bar',
      title: `Top ${groupCol} (Bar)`,
      groupBy: groupCol,
      metric: { column: countCol, aggregation: 'count' },
      limit: 10,
    },
    {
      id: genId('line'),
      datasetId,
      mode,
      kind: 'line',
      title: `Top ${groupCol} (Line)`,
      groupBy: groupCol,
      metric: { column: countCol, aggregation: 'count' },
      limit: 10,
    },
    {
      id: genId('pie'),
      datasetId,
      mode,
      kind: 'pie',
      title: `${groupCol} Distribution`,
      groupBy: groupCol,
      metric: { column: countCol, aggregation: 'count' },
      limit: 10,
    },
  ];
}

export const useWidgetsStore = create<WidgetsState>((set, get) => ({
  widgets: [],
  datasetId: null,
  mode: null,
  loaded: false,

  loadForDataset: async (id, columns, mode) => {
    set({ loaded: false, datasetId: id, mode, widgets: [] });
    let existing: WidgetConfig[] = [];
    try {
      existing = await configDb.widgets
        .where('[datasetId+mode]')
        .equals([id, mode])
        .toArray();
    } catch {
      existing = [];
    }

    if (existing.length > 0) {
      const migrated = migrateLegacyKinds(existing);
      if (migrated.changed.length > 0) {
        try {
          await configDb.widgets.bulkPut(migrated.changed);
        } catch {
          // Non-fatal: in-memory migration is still applied.
        }
      }
      // Guard against switching datasets / modes mid-await.
      if (get().datasetId !== id || get().mode !== mode) return;
      set({ widgets: migrated.all, loaded: true });
      return;
    }

    // Compare mode starts empty — the user configures the date column +
    // granularity before adding widgets (D-020 §Step 2).
    const defaults =
      mode === 'normal' ? buildDefaultWidgets(id, columns, mode) : [];
    if (defaults.length > 0) {
      try {
        await configDb.widgets.bulkPut(defaults);
      } catch {
        // Non-fatal: fall back to in-memory defaults.
      }
    }
    if (get().datasetId !== id || get().mode !== mode) return;
    set({ widgets: defaults, loaded: true });
  },

  addWidget: async (cfg) => {
    try {
      await configDb.widgets.put(cfg);
    } catch {
      // In-memory add still succeeds; persistence will retry on next change.
    }
    set({ widgets: [...get().widgets, cfg] });
  },

  updateWidget: async (id, patch) => {
    const widgets = get().widgets;
    const idx = widgets.findIndex((w) => w.id === id);
    if (idx === -1) return;
    const updated = { ...widgets[idx]!, ...patch, id };
    try {
      await configDb.widgets.put(updated);
    } catch {
      // Non-fatal.
    }
    const next = [...widgets];
    next[idx] = updated;
    set({ widgets: next });
  },

  removeWidget: async (id) => {
    try {
      await configDb.widgets.delete(id);
    } catch {
      // Non-fatal.
    }
    set({ widgets: get().widgets.filter((w) => w.id !== id) });
  },

  clear: () =>
    set({ widgets: [], datasetId: null, mode: null, loaded: false }),
}));
